import React from 'react';
import SendToSiteflowPopover from '@/components/SendToSiteflowPopover';

interface ProcessingResultsHeaderProps {
  processingStatus: string | undefined;
  processedAt: string | undefined;
  editMode: boolean;
  savingEdits: boolean;
  isFlowArray: boolean;
  resultsToDisplay: any | null;
  sendingToSiteflow: boolean;
  siteflowError?: string;
  siteflowMessage?: string;
  handleSendToSiteflow: (projectId: string, familyId: string) => void;
  toggleEditMode: () => void;
  saveEditedResults: () => void;
  cancelEditing: () => void;
}

const ProcessingResultsHeader: React.FC<ProcessingResultsHeaderProps> = ({
  processingStatus,
  processedAt,
  editMode,
  savingEdits,
  isFlowArray,
  resultsToDisplay,
  sendingToSiteflow,
  siteflowError,
  siteflowMessage,
  handleSendToSiteflow,
  toggleEditMode,
  saveEditedResults,
  cancelEditing,
}) => {
  return (
    <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">Processing Results</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {processingStatus === 'done'
              ? `Processed at ${processedAt ? new Date(processedAt).toLocaleString() : 'N/A'}`
              : processingStatus === 'error'
              ? 'Processing failed'
              : processingStatus === 'not_processed'
              ? 'Document has not been processed yet'
              : processingStatus ? `Status: ${processingStatus}` : 'Loading status...'}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {processingStatus === 'done' && isFlowArray && !editMode && (
            <SendToSiteflowPopover
              onConfirm={async (pId, fId) => handleSendToSiteflow(pId, fId)}
              sending={sendingToSiteflow}
              sendError={siteflowError}
              sendSuccess={siteflowMessage}
            >
              <button
                disabled={sendingToSiteflow}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  sendingToSiteflow
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {sendingToSiteflow ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : 'Send to Siteflow'}
              </button>
            </SendToSiteflowPopover>
          )}
          {resultsToDisplay && processingStatus === 'done' && (
            <button
              onClick={editMode ? saveEditedResults : toggleEditMode}
              disabled={savingEdits}
              className={`px-3 py-1 text-sm font-medium rounded ${
                editMode
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              } ${savingEdits ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {savingEdits ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : editMode ? 'Save Changes' : 'Edit Results'}
            </button>
          )}
          {editMode && (
            <button
              onClick={cancelEditing}
              className="px-3 py-1 text-sm font-medium rounded bg-gray-600 text-white hover:bg-gray-700"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessingResultsHeader;
